import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PropPosition,
  RgbaColor,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectAllPropPresets,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTake3D,
  selectSelectedTakeProps,
} from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import usePropHelper from "../../hooks/PropHelper";
import { Prop } from "../../models/Prop";
import { ISequence } from "../../models/interactions/Sequence";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import {
  applyPickedPropToInteraction,
  getSelectedPropInstanceId,
} from "../sequence/node_previews/SequencePropPicker.helpers";
import InteractionPointPreview, {
  PropPointStrategy,
} from "./InteractionPointPreview";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../sequence/EnvironmentPositionContext";

const selectedMarkerColor: RgbaColor = {
  r: 58 / 255,
  g: 196 / 255,
  b: 1,
  a: 1,
};

const supportedPropInteractionTypes = new Set<InteractionType>([
  InteractionType.InspectProp,
  InteractionType.WaitForClick,
  InteractionType.ClickIndicator,
  InteractionType.HideClickIndicator,
  InteractionType.Attach,
  InteractionType.Detach,
]);

export default function Viewport3DSequencePropPositionLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const currentTakeProps = useSelector(selectSelectedTakeProps);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const allPropPresets = useSelector(selectAllPropPresets);
  const { getSituationMetadata } = useSituationHelper();
  const { getUsedPropByInstanceId, getPropPresetByInstanceId } =
    usePropHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();

  const activeInteraction = useMemo(() => {
    return sequence?.nodes?.find((node) => node.id === picker.nodeId)?.data
      ?.interaction as Interaction | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "prop" &&
    !!activeInteraction &&
    supportedPropInteractionTypes.has(activeInteraction.type);

  const selectedPropInstanceId = getSelectedPropInstanceId(activeInteraction);

  const placedProps = useMemo(() => {
    const propsByPositionId = new Map(
      currentTakeProps.map((propTakeState) => [
        propTakeState.positionId,
        propTakeState,
      ]),
    );

    return getEnvironmentPositionContexts(
      takePositions ?? [],
      currentTake?.situations,
      getSituationMetadata,
    )
      .flatMap((context) =>
        context.propPositions.map((propPosition) => ({
          context,
          propPosition,
          positionId: getSituationPositionId(context, propPosition.id),
        })),
      )
      .map(({ context, propPosition, positionId }) => {
        const propTakeState =
          propsByPositionId.get(positionId) ??
          propsByPositionId.get(propPosition.id);
        if (!propTakeState) return undefined;

        const prop =
          allPropPresets.find(
            (candidate) => candidate.id === propTakeState.instanceId,
          ) ?? getPropPresetByInstanceId(propTakeState.instanceId);
        if (!prop) return undefined;

        return {
          propPosition,
          positionId,
          takeColor: context.takeColor,
          prop,
          usedProp: getUsedPropByInstanceId(propTakeState.instanceId),
          isSelected: selectedPropInstanceId === propTakeState.instanceId,
        };
      })
      .filter(
        (
          entry,
        ): entry is {
          propPosition: PropPosition;
          positionId: string;
          takeColor: RgbaColor | undefined;
          prop: Prop;
          usedProp: ReturnType<typeof getUsedPropByInstanceId>;
          isSelected: boolean;
        } => !!entry,
      );
  }, [
    allPropPresets,
    currentTake?.situations,
    currentTakeProps,
    getSituationMetadata,
    getPropPresetByInstanceId,
    getUsedPropByInstanceId,
    selectedPropInstanceId,
    takePositions,
  ]);

  if (!isVisible || !activeInteraction) {
    return null;
  }

  const getMarkerBorderColor = (isSelected: boolean, takeColor?: RgbaColor) => {
    return isSelected ? selectedMarkerColor : takeColor;
  };

  const getMarkerBackgroundColor = (
    isSelected: boolean,
    takeColor?: RgbaColor,
  ) => {
    return isSelected ? selectedMarkerColor : undefined;
  };

  const getMarkerIconColor = (isSelected: boolean) => {
    return isSelected ? { r: 1, g: 1, b: 1, a: 1 } : undefined;
  };

  return (
    <>
      {placedProps.map(
        ({
          propPosition,
          positionId,
          takeColor,
          prop,
          usedProp,
          isSelected,
        }) => (
          <InteractionPointPreview
            key={positionId}
            strategy={PropPointStrategy}
            x={propPosition.position.x}
            y={propPosition.position.y}
            prop={usedProp}
            markerBorderColor={getMarkerBorderColor(isSelected, takeColor)}
            markerBackgroundColor={getMarkerBackgroundColor(
              isSelected,
              takeColor,
            )}
            markerIconColor={getMarkerIconColor(isSelected)}
            directionDegrees={propPosition.direction?.x}
            directionColor={getMarkerBorderColor(isSelected, takeColor)}
            onClick={() => {
              updateInteraction(
                picker.nodeId,
                applyPickedPropToInteraction(
                  activeInteraction,
                  prop,
                  getUsedPropByInstanceId,
                ),
              );
              dispatch(closeSequencePositionPicker());
            }}
          />
        ),
      )}
    </>
  );
}
